import { WeekSchedule, DaySchedule } from "@types";

// Server format types
interface ServerDaySchedule {
  open: string | null;
  close: string | null;
  isClosed: boolean;
}

interface ServerSchedule {
  monday: ServerDaySchedule;
  tuesday: ServerDaySchedule;
  wednesday: ServerDaySchedule;
  thursday: ServerDaySchedule;
  friday: ServerDaySchedule;
  saturday: ServerDaySchedule;
  sunday: ServerDaySchedule;
}

/**
 * Convert server's officeHours format to frontend's WeekSchedule format
 */
export function convertServerScheduleToWeekSchedule(officeHours: ServerSchedule | null | undefined): WeekSchedule | undefined {
  if (!officeHours) return undefined;

  const convertDay = (serverDay: ServerDaySchedule): DaySchedule => {
    // If closed, return closed state
    if (serverDay.isClosed || (!serverDay.open && !serverDay.close)) {
      return {
        isOpen: false,
        openTime: undefined,
        closeTime: undefined,
        is24Hours: false
      };
    }

    // Check if 24 hours (could be "00:00" to "23:59" or similar)
    const is24Hours = serverDay.open === "00:00" && serverDay.close === "23:59";

    return {
      isOpen: true,
      openTime: serverDay.open || undefined,
      closeTime: serverDay.close || undefined,
      is24Hours: is24Hours
    };
  };

  return {
    monday: convertDay(officeHours.monday),
    tuesday: convertDay(officeHours.tuesday),
    wednesday: convertDay(officeHours.wednesday),
    thursday: convertDay(officeHours.thursday),
    friday: convertDay(officeHours.friday),
    saturday: convertDay(officeHours.saturday),
    sunday: convertDay(officeHours.sunday)
  };
}

/**
 * Convert frontend's WeekSchedule format to server's officeHours format
 */
export function convertWeekScheduleToServerFormat(weekSchedule: WeekSchedule): ServerSchedule {
  const convertDay = (day: DaySchedule): ServerDaySchedule => {
    // If closed or not open
    if (!day.isOpen) {
      return {
        open: null,
        close: null,
        isClosed: true
      };
    }

    // If 24 hours
    if (day.is24Hours) {
      return {
        open: "00:00",
        close: "23:59",
        isClosed: false
      };
    }

    // Regular hours - ensure both times are present
    if (!day.openTime || !day.closeTime) {
      // If open but times are missing, default to closed
      return {
        open: null,
        close: null,
        isClosed: true
      };
    }

    // Ensure time format is HH:MM
    const formatTime = (time: string): string => {
      // If already in HH:MM format, return as is
      if (/^\d{2}:\d{2}$/.test(time)) {
        return time;
      }
      // Try to parse and format
      const parts = time.split(':');
      if (parts.length === 2) {
        const hours = parts[0].padStart(2, '0');
        const minutes = parts[1].padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      return time;
    };

    return {
      open: formatTime(day.openTime),
      close: formatTime(day.closeTime),
      isClosed: false
    };
  };

  return {
    monday: convertDay(weekSchedule.monday),
    tuesday: convertDay(weekSchedule.tuesday),
    wednesday: convertDay(weekSchedule.wednesday),
    thursday: convertDay(weekSchedule.thursday),
    friday: convertDay(weekSchedule.friday),
    saturday: convertDay(weekSchedule.saturday),
    sunday: convertDay(weekSchedule.sunday)
  };
}

/**
 * Parse location data from server and convert schedules
 */
export function parseLocationData(serverData: any): any {
  const result = { ...serverData };

  // Convert officeHours to weekSchedule if it exists
  if (serverData.officeHours) {
    result.weekSchedule = convertServerScheduleToWeekSchedule(serverData.officeHours);
  }

  // You might also want to handle gateAccessHours similarly
  // if (serverData.gateAccessHours) {
  //   result.gateSchedule = convertServerScheduleToWeekSchedule(serverData.gateAccessHours);
  // }

  return result;
}